import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { type AppointmentListItem, type AppointmentStatus, filesApi } from '@/api';
import { AppointmentStatusBadge } from './AppointmentStatusBadge';
import { Button } from '@/components/ui/Button';
import { AuthContext } from '@/contexts/AuthContext';

interface AppointmentCardProps {
  appointment: AppointmentListItem;
  userRole: 'patient' | 'doctor';
  onCancel?: (id: string) => void;
  onReschedule?: (id: string) => void;
  onConfirm?: (id: string) => void;
  onJoin?: (id: string) => void;
  onPay?: (id: string) => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CANCELLABLE_STATUSES: AppointmentStatus[] = ['CREATED', 'WAITING_PAYMENT', 'PAID', 'WAITING_DOCTOR', 'DOCTOR_ACCEPTED'];
const RESCHEDULABLE_STATUSES: AppointmentStatus[] = ['CREATED', 'WAITING_PAYMENT', 'PAID', 'WAITING_DOCTOR', 'DOCTOR_ACCEPTED'];
const CONFIRMABLE_STATUSES: AppointmentStatus[] = ['CREATED', 'PAID'];
const JOINABLE_STATUSES: AppointmentStatus[] = ['DOCTOR_ACCEPTED'];

export function AppointmentCard({
  appointment,
  userRole,
  onCancel,
  onReschedule,
  onConfirm,
  onJoin,
  onPay,
}: AppointmentCardProps) {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const isDoctor = userRole === 'doctor' || user?.role === 'DOCTOR';
  const isPatient = !isDoctor;

  const isPendingPayment = appointment.status === 'WAITING_PAYMENT' || appointment.status === 'CREATED';
  const isInProgress = appointment.status === 'IN_PROGRESS';

  const canCancel = CANCELLABLE_STATUSES.includes(appointment.status);
  const canReschedule = RESCHEDULABLE_STATUSES.includes(appointment.status);
  const canConfirm = userRole === 'doctor' && CONFIRMABLE_STATUSES.includes(appointment.status);
  const canJoin = JOINABLE_STATUSES.includes(appointment.status) && appointment.canJoin;

  const handleJoin = () => {
    if (onJoin) {
      onJoin(appointment.id);
    } else {
      if (isDoctor) {
        navigate(appointment.joinUrl || `/medico/telemedicine/session/${appointment.id}`);
      } else {
        navigate(appointment.joinUrl || `/telemedicine/waiting/${appointment.id}`);
      }
    }
  };

  const handleEnterRoom = () => {
    if (onJoin) {
      onJoin(appointment.id);
    } else {
      if (isDoctor) {
        navigate(appointment.joinUrl || `/medico/telemedicine/session/${appointment.id}`);
      } else {
        navigate(appointment.joinUrl || `/telemedicine/video/${appointment.id}`);
      }
    }
  };

  const handlePay = () => {
    if (onPay) {
      onPay(appointment.id);
    } else {
      navigate(`/agendamentos/${appointment.id}`);
    }
  };

  const handleViewDetails = () => {
    if (isDoctor) {
      if (appointment.patient?.id) {
        navigate(`/medico/pacientes/${appointment.patient.id}/prontuarios`);
      } else {
        navigate('/medico/agenda');
      }
    } else {
      navigate(`/agendamentos/${appointment.id}`);
    }
  };

  const doctorName = appointment.doctor?.name || 'Médico';
  const doctorSpecialty = appointment.doctor?.specialty || 'Clínico Geral';
  const doctorCrm = appointment.doctor?.crm
    ? `CRM ${appointment.doctor.crm}/${appointment.doctor.crmState || ''}`
    : '';
  const doctorPhotoUrl = appointment.doctor?.photoUrl
    ? filesApi.getFileUrl(appointment.doctor.photoUrl)
    : null;

  const personName = isPatient ? doctorName : (appointment.patient?.name || 'Não informado');
  const personPhotoUrl = isPatient
    ? doctorPhotoUrl
    : (appointment.patient?.photoUrl ? filesApi.getFileUrl(appointment.patient.photoUrl) : null);

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts[parts.length - 1]?.[0] || '';
    if (first && last && parts.length >= 2) {
      return `${first}${last}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || 'M';
  };

  const initials = getInitials(personName);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden shrink-0">
          {personPhotoUrl ? (
            <img src={personPhotoUrl} alt={personName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-base font-semibold text-primary-700">
              {initials}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{personName}</h3>
          {isPatient ? (
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-500">
              <span>{doctorSpecialty}</span>
              {doctorCrm && (
                <>
                  <span className="text-gray-300">•</span>
                  <span>{doctorCrm}</span>
                </>
              )}
            </div>
          ) : (
            appointment.patient?.email && (
              <p className="text-sm text-gray-500 truncate">{appointment.patient.email}</p>
            )
          )}
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>{formatDate(appointment.scheduledAt)}</span>
            <span className="text-gray-300">|</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{formatTime(appointment.scheduledAt)}</span>
            <span className="text-gray-300">|</span>
            <span>{appointment.duration} min</span>
          </div>
          {appointment.reason && (
            <p className="mt-1 text-sm text-gray-500 truncate">{appointment.reason}</p>
          )}
        </div>

        {isPendingPayment ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Aguardando Pagamento
          </span>
        ) : isInProgress ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Em Andamento
          </span>
        ) : (
          <AppointmentStatusBadge status={appointment.status} size="sm" />
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleViewDetails}
        >
          Ver Detalhes
        </Button>
        {isPendingPayment && isPatient && (
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white font-medium"
            onClick={handlePay}
          >
            Pagar com PIX
          </Button>
        )}
        {isInProgress && (
          <Button
            size="sm"
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium animate-pulse"
            onClick={handleEnterRoom}
          >
            Entrar na Sala
          </Button>
        )}
        {canJoin && !isInProgress && (
          <Button size="sm" className="flex-1" onClick={handleJoin}>
            Entrar na consulta
          </Button>
        )}
        {canConfirm && onConfirm && (
          <Button size="sm" variant="primary" onClick={() => onConfirm(appointment.id)}>
            Confirmar Consulta
          </Button>
        )}
        {canReschedule && onReschedule && (
          <Button size="sm" variant="outline" onClick={() => onReschedule(appointment.id)}>
            Reagendar
          </Button>
        )}
        {canCancel && onCancel && (
          <Button size="sm" variant="ghost" onClick={() => onCancel(appointment.id)}>
            Cancelar
          </Button>
        )}
      </div>
    </div>
  );
}
